import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CertificateDetail } from '../../models/verify-signature-response.model';
import { dnCommonName, parseDnAttributes } from '../../core/utils/cert-subject.utils';

export interface SignatureValidationModalData {
  valid: boolean;
  signerName: string;
  cert?: CertificateDetail;
  documentValid?: boolean;
  /** Fecha de la última verificación (tipo Adobe "Última comprobación") */
  lastCheckDate?: Date | string | null;
}

@Component({
  selector: 'app-signature-validation-modal',
  templateUrl: './signature-validation-modal.component.html',
  styleUrls: ['./signature-validation-modal.component.css'],
  standalone: false
})
export class SignatureValidationModalComponent {
  detailsOpen = false;

  constructor(
    public dialogRef: MatDialogRef<SignatureValidationModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: SignatureValidationModalData
  ) {}

  get valid(): boolean {
    return this.data?.valid ?? false;
  }

  get signerName(): string {
    return this.data?.signerName ?? '';
  }

  get cert(): CertificateDetail | undefined {
    return this.data?.cert;
  }

  get lastCheckDate(): Date | string | null | undefined {
    return this.data?.lastCheckDate;
  }

  get issuerSummary(): string {
    const emisor = this.cert?.emisor;
    if (!emisor || emisor === 'N/A') return '';
    const cn = dnCommonName(emisor);
    const org = parseDnAttributes(emisor).find((a) => a.key.toUpperCase() === 'O')?.value;
    if (cn && org && org !== cn) return `${cn} · ${org}`;
    return cn || emisor;
  }

  get timestampDetailText(): string {
    const cert = this.cert;
    if (cert?.timestamped) {
      return cert.timestampDate
        ? `La hora de la firma procede de una estampa de tiempo confiable (${cert.timestampDate}).`
        : 'La hora de la firma procede de una estampa de tiempo confiable (TSA).';
    }
    return 'La hora de la firma procede del reloj del equipo del firmante.';
  }

  get ltvDetailText(): string {
    const cert = this.cert;
    if (cert?.ltvEnabled === true) {
      return 'La firma incluye evidencia LTV embebida en el documento.';
    }
    if (cert?.ltvEnabled === null || cert?.ltvEnabled === undefined) {
      return 'LTV no aplica para este tipo de firma.';
    }
    return `La firma no está activada para LTV y caducará después de ${this.formatValidoHastaForLTV(cert?.validoHasta)}.`;
  }

  toggleDetails(): void {
    this.detailsOpen = !this.detailsOpen;
  }

  formatLastCheckDate(date: Date | string | null | undefined): string {
    if (date == null) return '—';
    const d = typeof date === 'string' ? new Date(date) : date;
    if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '—';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const h = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    const s = String(d.getSeconds()).padStart(2, '0');
    const tz = -d.getTimezoneOffset();
    const tzSign = tz >= 0 ? '+' : '-';
    const tzMin = Math.abs(tz) % 60;
    const tzHour = Math.floor(Math.abs(tz) / 60);
    return `${y}.${m}.${day} ${h}:${min}:${s} ${tzSign}${String(tzHour).padStart(2, '0')}'${String(tzMin).padStart(2, '0')}'`;
  }

  formatValidoHastaForLTV(validoHasta: string | null | undefined): string {
    if (!validoHasta || validoHasta === 'N/A') return '—';
    const d = new Date(validoHasta);
    if (Number.isNaN(d.getTime())) return validoHasta;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}/${m}/${day}`;
  }

  close(): void {
    this.dialogRef.close();
  }

  openCertificateProperties(): void {
    this.dialogRef.close({ openProperties: true, cert: this.data?.cert });
  }
}
